/**
 * Flight Filters Component
 * Handles filtering functionality for flight search results
 */

import { FlightResult, FlightFilters } from '../types/data-models';

export class FlightFiltersComponent {
  private container: HTMLElement;
  private currentFilters: FlightFilters = {};
  private availableAirlines: string[] = [];
  private priceRange = { min: 0, max: 1000 };

  public onFilterChange: ((filters: FlightFilters) => void) | null = null;
  public onClearFilters: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.bindEvents();
  }

  private bindEvents(): void {
    // Price range inputs
    const priceMinInput = this.container.querySelector('#price-min') as HTMLInputElement;
    const priceMaxInput = this.container.querySelector('#price-max') as HTMLInputElement;

    if (priceMinInput && priceMaxInput) {
      const updatePriceFilter = () => {
        const min = parseInt(priceMinInput.value) || this.priceRange.min;
        const max = parseInt(priceMaxInput.value) || this.priceRange.max;
        
        this.currentFilters.priceRange = {
          min: Math.max(min, this.priceRange.min),
          max: Math.min(max, this.priceRange.max),
          currency: 'EUR'
        };
        
        this.applyFilters();
      };

      priceMinInput.addEventListener('change', updatePriceFilter);
      priceMaxInput.addEventListener('change', updatePriceFilter);
    }

    // Stops filter
    const stopsCheckboxes = this.container.querySelectorAll('input[name="stops"]');
    stopsCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateStopsFilter();
      });
    });

    // Departure time filter
    const timeCheckboxes = this.container.querySelectorAll('input[name="departureTime"]');
    timeCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateDepartureTimeFilter();
      });
    });

    // Clear filters button
    const clearButton = this.container.querySelector('.search-filters__clear');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }
  }

  public updateAvailableFilters(flights: FlightResult[]): void {
    this.updatePriceRange(flights);
    this.updateAvailableAirlines(flights);
    this.renderAirlinesFilter();
  }

  private updatePriceRange(flights: FlightResult[]): void {
    if (flights.length === 0) return;

    const prices = flights.map(flight => flight.price.min);
    this.priceRange.min = Math.min(...prices);
    this.priceRange.max = Math.max(...prices);

    // Update price input placeholders
    const priceMinInput = this.container.querySelector('#price-min') as HTMLInputElement;
    const priceMaxInput = this.container.querySelector('#price-max') as HTMLInputElement;

    if (priceMinInput && priceMaxInput) {
      priceMinInput.placeholder = this.priceRange.min.toString();
      priceMaxInput.placeholder = this.priceRange.max.toString();
      priceMinInput.min = this.priceRange.min.toString();
      priceMaxInput.max = this.priceRange.max.toString();
    }
  }

  private updateAvailableAirlines(flights: FlightResult[]): void {
    const airlineCount: { [key: string]: number } = {};
    
    flights.forEach(flight => {
      airlineCount[flight.airline] = (airlineCount[flight.airline] || 0) + 1;
    });

    this.availableAirlines = Object.keys(airlineCount).sort();
  }

  private renderAirlinesFilter(): void {
    const airlinesContainer = this.container.querySelector('#airlines-filter');
    if (!airlinesContainer) return;

    const airlinesHtml = this.availableAirlines.map(airline => `
      <label class="filter-checkbox">
        <input type="checkbox" name="airlines" value="${airline}" class="filter-checkbox__input">
        <span class="filter-checkbox__label">${airline}</span>
      </label>
    `).join('');

    airlinesContainer.innerHTML = airlinesHtml;

    // Bind events to new checkboxes
    const airlineCheckboxes = airlinesContainer.querySelectorAll('input[name="airlines"]');
    airlineCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateAirlinesFilter();
      });
    });
  }

  private updateAirlinesFilter(): void {
    const checkedAirlines = Array.from(
      this.container.querySelectorAll('input[name="airlines"]:checked')
    ).map(checkbox => (checkbox as HTMLInputElement).value);

    if (checkedAirlines.length > 0) {
      this.currentFilters.airlines = checkedAirlines;
    } else {
      delete this.currentFilters.airlines;
    }

    this.applyFilters();
  }

  private updateStopsFilter(): void {
    const checkedStops = Array.from(
      this.container.querySelectorAll('input[name="stops"]:checked')
    ).map(checkbox => {
      const value = (checkbox as HTMLInputElement).value;
      return value === '2+' ? 2 : parseInt(value);
    });

    if (checkedStops.length > 0) {
      this.currentFilters.stops = checkedStops;
    } else {
      delete this.currentFilters.stops;
    }

    this.applyFilters();
  }

  private updateDepartureTimeFilter(): void {
    const checkedTimes = Array.from(
      this.container.querySelectorAll('input[name="departureTime"]:checked')
    ).map(checkbox => (checkbox as HTMLInputElement).value);

    if (checkedTimes.length > 0) {
      this.currentFilters.departureTimeRanges = checkedTimes;
    } else {
      delete this.currentFilters.departureTimeRanges;
    }

    this.applyFilters();
  }

  private applyFilters(): void {
    if (this.onFilterChange) {
      this.onFilterChange(this.currentFilters);
    }
  }

  public clearAllFilters(): void {
    this.currentFilters = {};

    // Clear all checkboxes
    const checkboxes = this.container.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      (checkbox as HTMLInputElement).checked = false;
    });

    // Clear price inputs
    const priceMinInput = this.container.querySelector('#price-min') as HTMLInputElement;
    const priceMaxInput = this.container.querySelector('#price-max') as HTMLInputElement;

    if (priceMinInput) priceMinInput.value = '';
    if (priceMaxInput) priceMaxInput.value = '';

    if (this.onClearFilters) {
      this.onClearFilters();
    }

    this.applyFilters();
  }

  public getActiveFilters(): FlightFilters {
    return { ...this.currentFilters };
  }

  public setFilters(filters: FlightFilters): void {
    this.currentFilters = { ...filters };
    this.updateUI();
  }

  private updateUI(): void {
    // Update price inputs
    if (this.currentFilters.priceRange) {
      const priceMinInput = this.container.querySelector('#price-min') as HTMLInputElement;
      const priceMaxInput = this.container.querySelector('#price-max') as HTMLInputElement;

      if (priceMinInput) priceMinInput.value = this.currentFilters.priceRange.min.toString();
      if (priceMaxInput) priceMaxInput.value = this.currentFilters.priceRange.max.toString();
    }

    // Update airline checkboxes
    if (this.currentFilters.airlines) {
      this.currentFilters.airlines.forEach(airline => {
        const checkbox = this.container.querySelector(`input[name="airlines"][value="${airline}"]`) as HTMLInputElement;
        if (checkbox) checkbox.checked = true;
      });
    }

    // Update stops checkboxes
    if (this.currentFilters.stops) {
      this.currentFilters.stops.forEach(stops => {
        const value = stops >= 2 ? '2+' : stops.toString();
        const checkbox = this.container.querySelector(`input[name="stops"][value="${value}"]`) as HTMLInputElement;
        if (checkbox) checkbox.checked = true;
      });
    }

    // Update departure time checkboxes
    if (this.currentFilters.departureTimeRanges) {
      this.currentFilters.departureTimeRanges.forEach(timeRange => {
        const checkbox = this.container.querySelector(`input[name="departureTime"][value="${timeRange}"]`) as HTMLInputElement;
        if (checkbox) checkbox.checked = true;
      });
    }
  }

  public getFilterSummary(): string {
    const filters: string[] = [];

    if (this.currentFilters.priceRange) {
      filters.push(`Prix: €${this.currentFilters.priceRange.min} - €${this.currentFilters.priceRange.max}`);
    }

    if (this.currentFilters.airlines && this.currentFilters.airlines.length > 0) {
      filters.push(`Compagnies: ${this.currentFilters.airlines.join(', ')}`);
    }

    if (this.currentFilters.stops && this.currentFilters.stops.length > 0) {
      const stopsText = this.currentFilters.stops.map(stops => {
        if (stops === 0) return 'Direct';
        if (stops === 1) return '1 escale';
        return `${stops}+ escales`;
      }).join(', ');
      filters.push(`Escales: ${stopsText}`);
    }

    if (this.currentFilters.departureTimeRanges && this.currentFilters.departureTimeRanges.length > 0) {
      const timeLabels: { [key: string]: string } = {
        'morning': 'Matin',
        'afternoon': 'Après-midi',
        'evening': 'Soir',
        'night': 'Nuit'
      };
      const timesText = this.currentFilters.departureTimeRanges
        .map(time => timeLabels[time] || time)
        .join(', ');
      filters.push(`Horaires: ${timesText}`);
    }

    return filters.length > 0 ? filters.join(' • ') : 'Aucun filtre actif';
  }

  public hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).length > 0;
  }
}